import { AssetVizController } from '@asset-viz/defines'
import { ForecastAssetVizWindow } from 'features/assetVizAdapter/ForecastAssetVizWindow'
import { getCoarsestCdf } from 'features/assetVizAdapter/forecastAssetVizWindow/resolutionOptions'
import { FFS_INDICATION_LAYER_PREFIX } from 'features/libra/stepComponents/ffs/ffsIndicationMarkup'
import { useFfsComponentCdfs } from 'features/libra/stepComponents/ffs/useFfsComponentCdfs'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useFfsWorkspaceState } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { layerBoundingRect } from 'features/libra/workspacePanels/ffsLayerBounds'
import { FfsLayerVisibilityControl } from 'features/libra/workspacePanels/FfsLayerVisibilityControl'
import { useFfsLayerViewState } from 'features/libra/workspacePanels/useFfsLayerViewState'
import { useGeckoLocale } from 'features/locale'
import { FC, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { GeckoFeature, GeckoMarkupPrimitive } from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  border-right: 1px solid
    ${({ theme }) => theme.forecast.border ?? theme.forecast.border2};
`

const Empty = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  padding: 1rem;
  text-align: center;
`

const VizFlexHost = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;

  & > *:not([data-overlay]) {
    flex: 1 1 auto;
    min-height: 0;
  }
`

/**
 * FFS-only workspace panel that renders the embedded `ForecastAssetVizWindow`
 * for the ticket's (component, inspection) pair. Subscribes to
 * `useFfsWorkspaceState()` for cross-pane coupling and bridges selection /
 * onSelected between the viz and the workspace state.
 *
 * CDF discovery is shared with `useObservedMinThickness` via
 * `useFfsComponentCdfs` -- both surfaces hit the same SWR cache key.
 */
export const Component: FC = () => {
  const { componentId, inspectionId } = useFfsTicketContext()

  const { cdfs, assetModel, inspection, component, isLoading } =
    useFfsComponentCdfs({
      componentId,
      inspectionId,
    })

  const {
    selection,
    setSelection,
    assetVizMode,
    ffsMarkupLayers,
    coordinateMarkupLayers,
    primaryCdf,
    setPrimaryCdf,
    setSelectedIndicationId,
    zoomToLayerId,
    setZoomToLayerId,
  } = useFfsWorkspaceState()

  const vizRef = useRef<AssetVizController>(null)

  const { getPreferredMeasurementUnit } = useGeckoLocale()

  // Panel-local layer view-state (which layers are hidden/hovered) + the derived
  // viewer array and control props. Kept out of the workspace context so hover
  // churn stays local to this panel + its overlay control.
  const { markupLayers, layerControlProps } = useFfsLayerViewState({
    ffsMarkupLayers,
    coordinateMarkupLayers,
    vizRef,
  })

  // Default to the COARSEST (lowest-definition / largest bin) CDF within the
  // resolution family that would otherwise be shown (`cdfs[0]`). High-definition
  // bins carry far more data and can lag the user's machine; users can still pick
  // a finer resolution from the viewer's Resolution dropdown. Anchoring to
  // `cdfs[0]` preserves which measure/view is shown and changes only the bin size.
  const defaultCdf = useMemo(
    () =>
      getCoarsestCdf({
        cdfs,
        selectedCDF: cdfs[0],
        getPreferredMeasurementUnit,
      }),
    [cdfs, getPreferredMeasurementUnit]
  )

  // Mirror the default CDF into workspace state as `primaryCdf` (single source
  // of truth for the active CDF -- the triage step uses it as a fallback for
  // ruler coord conversion). Only seed when unset or stale so a user's manual
  // resolution pick is never overridden.
  useEffect(() => {
    if (cdfs.length === 0) {
      if (primaryCdf !== undefined) setPrimaryCdf(undefined)
      return
    }
    if (!primaryCdf || !cdfs.some((c) => c.id === primaryCdf.id)) {
      setPrimaryCdf(defaultCdf)
    }
  }, [cdfs, defaultCdf, primaryCdf, setPrimaryCdf])

  // Zoom the viz to a ruler/markup when the detail panel requests it (Distances
  // card "zoom to"). Keyed on ffsMarkupLayers so an early request (before the
  // ruler layer has loaded) is retried, not dropped; only clear after we frame
  // it (or the layer resolved but has no boundable shape).
  useEffect(() => {
    if (!zoomToLayerId) return
    const layer = ffsMarkupLayers.find((l) => l.id === zoomToLayerId)
    if (!layer || !vizRef.current) return
    const rect = layerBoundingRect(layer)
    if (rect) vizRef.current.zoomToFit(rect).catch(() => {})
    setZoomToLayerId(null)
  }, [zoomToLayerId, ffsMarkupLayers, setZoomToLayerId])

  const sceneConfig = useMemo(
    () => ({
      showFeatures: true,
      showFeatureLabels: true,
      showRulers: true,
      showCompass: false,
      showLegend: true,
    }),
    []
  )

  const handleSelected = (item: unknown) => {
    if (item === null || item === undefined) {
      setSelection(undefined)
      return
    }
    // Indication rects are emitted as GeckoMarkupPrimitive. The primitive's id
    // matches the indication's Rolodex id (see ffsIndicationMarkup.ts) -- we
    // confirm by matching the indication-layer naming convention before
    // pushing to the workspace, so CTPs / rulers don't trigger drawer-opens.
    if (item instanceof GeckoMarkupPrimitive) {
      const primitiveId = (item as { id?: string }).id
      if (
        primitiveId &&
        ffsMarkupLayers.some(
          (l) => l.id === `${FFS_INDICATION_LAYER_PREFIX}${primitiveId}`
        )
      ) {
        setSelectedIndicationId(primitiveId)
      }
      return
    }
    // Otherwise (GeckoFeature or GeckoComponentCoords) forward verbatim.
    setSelection(item as GeckoFeature)
  }

  if (!assetModel || !component || !inspection) {
    let emptyMessage: string
    if (!componentId || !inspectionId) {
      emptyMessage = 'Ticket is missing required component or inspection tags.'
    } else if (isLoading) {
      emptyMessage = 'Loading asset visualization...'
    } else if (!component) {
      emptyMessage = 'Component entry not found in Rolodex.'
    } else if (!inspection) {
      emptyMessage = 'Inspection entry not found in Rolodex.'
    } else {
      emptyMessage = 'No asset model found for this unit.'
    }
    return (
      <Container>
        <Empty>{emptyMessage}</Empty>
      </Container>
    )
  }

  if (cdfs.length === 0) {
    return (
      <Container>
        <Empty>
          No component data frames found for this inspection. The viewer will
          populate once data has been ingested.
        </Empty>
      </Container>
    )
  }

  // `cdfs` is non-empty here (guarded above), so `activeCdf` is always defined:
  // the user's manual pick, else the coarsest default, else the first CDF.
  const activeCdf = primaryCdf ?? defaultCdf ?? cdfs[0]

  return (
    <Container>
      <VizFlexHost>
        <ForecastAssetVizWindow
          ref={vizRef}
          assetModel={assetModel}
          components={[component]}
          selectedInspection={inspection}
          selectedCDF={activeCdf}
          resolutionDataFrames={cdfs}
          componentDataFrames={[activeCdf]}
          onSelectedCDFChange={setPrimaryCdf}
          sceneConfig={sceneConfig}
          showUnitsToggle
          mode={assetVizMode}
          markupLayers={markupLayers}
          selection={selection}
          onSelected={handleSelected}
          topLeftContent={
            <FfsLayerVisibilityControl
              layers={ffsMarkupLayers}
              {...layerControlProps}
            />
          }
          expandable
          expandedTitle={`${component.data.name ?? component.id} -- ${inspection.data.name ?? inspection.id}`}
        />
      </VizFlexHost>
    </Container>
  )
}
